import React from 'react';
import { Card, CardContent, Typography, Box, Divider, CardMedia, Grid, Button, darken } from '@mui/material';
import styled from '@emotion/styled';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import AccessTime from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';

import { formatGenericDate } from '../../../utils/util';

import { SectionHeaderBar, BasicFlex, LabelUpper } from '../../../utils/Common';

import { useFormikContext, FieldArray } from 'formik';

import Field from '../../../utils/FormikFields/Field';

import AutoCompleteArray from '../../../utils/FormikFields/AutoCompleteArray';

import AddRepairs from './AddRepairs';

import useIsMobile from '../../../hooks/useIsMobile';

// linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);

const Container = styled.div`
  background-color: ${props => props.theme.colors.softBg};
  border-radius: 5px;
  padding: .7rem 1rem;
`

const SearchContainer = styled.div`
  margin: .5rem 0;
`
const ServiceListContainer = styled(BasicFlex)`
  margin-top: 1rem;
  width: 100%;
  gap: 1rem;
`;

const StyledCard = styled(Card)`
 border-radius: 8px;
 border: 1px solid ${props => props.theme.colors.border};
 box-shadow: 0 1px 5px rgba(0, 0, 0, 0.12);
 transition: all 0.2s ease-in-out;
 overflow: hidden;
 
 &:hover {
   border-color: ${props => props.theme.colors.darkBg};
   box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
 }
`;

const ServiceCard = styled(BasicFlex)`
  height: 100%;
  align-items: stretch;
`;

const ImageContainer = styled(BasicFlex)`
  background-image: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  border-radius: 5px;
  border-right: 1px solid ${props => props.theme.colors.border};
  width: 12rem;
  justify-content: center;
  align-items: center;
`;

const ServiceImage = styled('img')`
 width: 100%;
 height: 100%;
 object-fit: cover;
`;

const ServiceItemDetails = styled(BasicFlex)`
  /* min-width: fit-content; */
`;

const ServiceTitle = styled.div`
  text-transform: uppercase;
  font-weight: 600;
  font-size: 1.6rem;
  /* color: ${props => props.theme.colors.mildText}; */
  /* margin-bottom: .2rem; */
`

const DetailLineStyles = styled(BasicFlex)`
  /* font-size: 1.4rem; */
  color: ${props => props.theme.colors.mildText};
`;

const PriceDisplay = styled.div`
  height: min-content;

  .MuiInputBase-input {
    padding: .2rem !important;
  }
`

const ServiceActions = styled(BasicFlex)`

`;



const DetailLine = ({ Icon, value}) => {
  return (
    <DetailLineStyles justify="flex-start" gap="1rem">
      {Icon ? <Icon fontSize='small' color="icon" /> : null}
      {value}
    </DetailLineStyles>
  )
}


const ServiceLine = ({ service = {}, index, remove }) => {
  let { image, name, duration, wig_id, service_date, scheduled_date, description, service_ticket_id, stylist, stylist_id } = service;

  const { isMobile } = useIsMobile();

  return (
    <StyledCard sx={{p: "0 !important", width: "100%"}} variant='outlined'>
      <ServiceCard justify="flex-start">
        <ImageContainer align="stretch">
        {image ? (
           <ServiceImage src={image} alt={name} />
         ) : (
           <SettingsSuggestRoundedIcon sx={{ fontSize: 48, color: '#bdbdbd' }} />
         )}
        </ImageContainer>
        <CardContent sx={{p: ".5rem 1.6rem !important", width: "100%"}}>
         <BasicFlex justify="space-between" dir={isMobile ? "column" : "row"} gap={isMobile ? "1.5rem" : ""}>
          
          <ServiceItemDetails dir="column" gap=".6rem">
            <ServiceTitle>{name}</ServiceTitle>
            {description ? <DetailLine value={description} /> : null}
            {stylist ? <DetailLine Icon={ManageAccountsRoundedIcon} value={stylist} /> : null}
            {wig_id ? <DetailLine Icon={SellRoundedIcon} value={wig_id} /> : null}
            {service_date ? <DetailLine Icon={CalendarMonthRoundedIcon} value={formatGenericDate(service_date || new Date())} /> : null}
            <DetailLine Icon={AccessTime} value={duration} />
          </ServiceItemDetails>

          {service_ticket_id && (
            <AddRepairs 
              stylist={stylist_id}
              wig={{wig_id: wig_id, description: ""}} 
              source="service"
            />
          )}
          {/* <AddRepairs service_ticket_id={service_ticket_id} /> */}

          <ServiceActions dir="column" align={isMobile ? "flex-start" : "flex-end"} justify="space-between" gap={isMobile ? "1rem" : "3.5rem"}>
            <PriceDisplay>
              <Field name={`services.${index}.rate`} type="number" InputProps={{sx: {margin: 0, width: "9rem", paddingLeft: ".5rem !important", fontSize: "1.8rem", fontWeight: "500", gap: ".5rem"}, startAdornment: <AttachMoneyRoundedIcon fontSize='small' />}} />
            </PriceDisplay>
            <BasicFlex width="8rem">
              <Button size="small" color="basic" startIcon={<DeleteForeverRoundedIcon />} onClick={() => remove(index)}>
                Remove
              </Button> 
              
            </BasicFlex>

          </ServiceActions>
         </BasicFlex>
        </CardContent>
      </ServiceCard>
      
    </StyledCard>
  )
}

const Services = () => {
  const { values } = useFormikContext() || {};
  let updates = [
    {name: "service_id", field: "service_id"},
    {name: "rate", field: "default_price"},
    {name: "duration", field: "default_duration_in_minutes"},
    {name: "name", field: "name"},
    {name: "description", field: "description"},
    {name: "service_date", field: "scheduled_date"},
  ];
  
  return (
    <Grid size={{xs: 12}}>
      <Container>
        <LabelUpper>Services</LabelUpper>
          <FieldArray name="services">
            {({ push, remove }) => (
              <>
               <SearchContainer>
                  <AutoCompleteArray fieldName="name" name="add_services" label="Select Services" url="services" placeholder='Add Services' push={push} formUpdates={updates} additionalFields={{quantity: 1, item_type: "service"}} disabled={!values?.customer_id} />
               </SearchContainer>
              {values.services.length ? <ServiceListContainer dir="column">
                {values.services.map((service, index) => (
                  <ServiceLine key={index} service={service} index={index} remove={remove} />
                ))}
              </ServiceListContainer> : null}
              </>
            )}
          </FieldArray>
          {/* <ServiceLine image="" title="Wash & Set" /> */}
          
      </Container>
    </Grid>
  )
};

export default Services;